// FO-044-P1 (batch-16) — uniforme, sorteerbare export-bestandsnaam.
//
// Eén gedeelde bron voor de bestandsnaam van álle data-exports (de export-engine
// + de ad-hoc exporters: relaties, bellijst, acquisitie): `wat_scope_periode.ext`.
// Bewust een PURE module (geen DB, geen deps) zodat zowel de api (engine) als de
// frontends (client-side download-fallbacks) 'm kunnen importeren.
//
// Eigenschappen (FO-044-P1 §6/§11):
//   - Sorteerbaar: de periode-vorm is `2026-W24` (ISO-week) / `2026-06` (maand) /
//     `2026-06-01_2026-06-30` (datumbereik) / `2026-06-15` (losse dag).
//   - ASCII-/RFC 6266-veilig: diacrieten gestript, alle overige tekens → `-`.
//   - Geen lege of dubbele scheidingstekens: lege segmenten vallen weg, de rest
//     wordt met `_` verbonden.
//   - Week/maand worden uit de losse kalenderdatums (yyyy-mm-dd) afgeleid; de
//     ISO-week is tijdzone-onafhankelijk voor een kale kalenderdatum.

// ─── Segment-sanitisatie ──────────────────────────────────────────────────────

/** Strip combineerbare diacriet-tekens (é → e, ü → u) via NFD-normalisatie. */
function stripDiacritics(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '')
}

/**
 * Maak één naam-segment (wat/scope) ASCII-veilig: diacrieten weg, alle tekens
 * buiten `[A-Za-z0-9-]` → `-`, dubbele koppeltekens samengevoegd en
 * leidende/sluitende koppeltekens verwijderd. Lege input → lege string.
 *
 * FO-075: dé gedeelde segment-sanitizer. `apps/api/src/lib/filename.ts`
 * re-exporteert deze zodat de download-/patroon-namen dezelfde vorm krijgen.
 */
export function sanitizeSegment(raw: string | null | undefined): string {
  const s = stripDiacritics((raw ?? '').trim())
  return s
    .replace(/[^A-Za-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Sanitiseer het periode-segment. Dit segment wordt door `formatExportPeriode`
 * gebouwd en bevat bewust een `_` voor het datumbereik (`van_tot`) - die mag
 * dus NIET verloren gaan. We behouden `[A-Za-z0-9_-]`, strippen diacrieten en
 * schonen dubbele/randtekens op.
 */
export function sanitizePeriode(raw: string | null | undefined): string {
  const s = stripDiacritics((raw ?? '').trim())
  return s
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-_]+|[-_]+$/g, '')
}

// ─── Periode-formatter ──────────────────────────────────────────────────────

interface Ymd {
  y: number
  m: number
  d: number
}

/** Parse 'yyyy-mm-dd' naar componenten; `null` bij ongeldig/niet-bestaand. */
function parseYmd(s: string): Ymd | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim())
  if (!m) return null
  const y = parseInt(m[1]!, 10)
  const mo = parseInt(m[2]!, 10)
  const d = parseInt(m[3]!, 10)
  const check = new Date(Date.UTC(y, mo - 1, d))
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) {
    return null
  }
  return { y, m: mo, d }
}

/**
 * ISO-8601 weeknummer (week 1 bevat de eerste donderdag) als 'yyyy-Www'. Zelfde
 * algoritme als `isoWeekLabel` in `apps/api/src/lib/filename.ts`, zodat de
 * export-periode en de werkbon-/factuur-weeknummers identiek zijn.
 */
function isoWeekLabelFromYmd(p: Ymd): string {
  const tmp = new Date(Date.UTC(p.y, p.m - 1, p.d))
  const dayOfWeek = tmp.getUTCDay() || 7
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayOfWeek)
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1))
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  return `${tmp.getUTCFullYear()}-W${String(week).padStart(2, '0')}`
}

/** Is [van, tot] precies één ISO-week (maandag t/m de zondag 6 dagen later)? */
function isFullIsoWeek(van: Ymd, tot: Ymd): boolean {
  const vanMs = Date.UTC(van.y, van.m - 1, van.d)
  const totMs = Date.UTC(tot.y, tot.m - 1, tot.d)
  const vanDow = new Date(vanMs).getUTCDay() // 1 = maandag
  const totDow = new Date(totMs).getUTCDay() // 0 = zondag
  const diffDays = (totMs - vanMs) / 86400000
  return vanDow === 1 && totDow === 0 && diffDays === 6
}

/** Is [van, tot] precies één kalendermaand (de 1e t/m de laatste dag)? */
function isFullCalendarMonth(van: Ymd, tot: Ymd): boolean {
  if (van.d !== 1 || van.y !== tot.y || van.m !== tot.m) return false
  const lastDay = new Date(Date.UTC(tot.y, tot.m, 0)).getUTCDate()
  return tot.d === lastDay
}

/**
 * Sorteerbare periode-vorm uit een datumbereik (kale 'yyyy-mm-dd'-strings):
 *   - precies één ISO-week → `2026-W24`
 *   - precies één kalendermaand → `2026-06`
 *   - losse dag (van === tot) → `2026-06-15`
 *   - overig bereik → `2026-06-01_2026-06-30`
 * Ontbrekende/ongeldige grenzen vallen terug op de ruwe waarde(n).
 */
export function formatExportPeriode(
  van?: string | null,
  totEnMet?: string | null,
): string {
  const v = (van ?? '').trim()
  const t = (totEnMet ?? '').trim()
  if (!v && !t) return ''
  const pv = v ? parseYmd(v) : null
  const pt = t ? parseYmd(t) : null
  if (!pv || !pt) {
    return v && t ? `${v}_${t}` : v || t
  }
  if (v === t) return v
  if (isFullIsoWeek(pv, pt)) return isoWeekLabelFromYmd(pv)
  if (isFullCalendarMonth(pv, pt)) return `${pv.y}-${String(pv.m).padStart(2, '0')}`
  return `${v}_${t}`
}

// ─── Datum-segment (FO-075) ───────────────────────────────────────────────────

/**
 * Sorteerbare datum `yyyyMMdd` voor in een bestandsnaam, afgeleid uit een kale
 * kalenderdatum (`yyyy-MM-dd`, tijdzone-onafhankelijk) of een `Date` (dan in
 * **Europe/Amsterdam**, zodat de exportdag nooit een dag verschuift). Reeds
 * compacte `yyyyMMdd` blijft staan; lege/ongeldige invoer → lege string (het
 * segment valt dan weg).
 */
export function formatStandardDatum(input?: Date | string | null): string {
  if (input == null) return ''
  if (typeof input === 'string') {
    const s = input.trim()
    if (!s) return ''
    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
    if (ymd) return `${ymd[1]}${ymd[2]}${ymd[3]}`
    const compact = /^(\d{8})$/.exec(s)
    if (compact) return compact[1]!
    return sanitizeSegment(s)
  }
  if (Number.isNaN(input.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(input)
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? ''
  const y = get('year')
  const m = get('month')
  const d = get('day')
  return y && m && d ? `${y}${m}${d}` : ''
}

/**
 * FO-303-P2 — de aanmaak-/exportdatum als leesbare, sorteerbare `yyyy-MM-dd`
 * (met koppeltekens, bv. `2026-07-08`), afgeleid uit een kale kalenderdatum of
 * een `Date` (dan in **Europe/Amsterdam**, zodat de exportdag nooit een dag
 * verschuift). Bewust mét koppeltekens (i.t.t. het compacte `[datum]`-token
 * `yyyyMMdd`) omdat FO-303 het aanmaakdatum-deel expliciet als `yyyy-MM-dd` wil.
 * Leeg/ongeldig → lege string (het segment valt dan weg).
 */
export function formatAanmaakdatum(input?: Date | string | null): string {
  if (input == null) return ''
  if (typeof input === 'string') {
    const s = input.trim()
    if (!s) return ''
    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
    if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`
    const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(s)
    if (compact) return `${compact[1]}-${compact[2]}-${compact[3]}`
    return sanitizeSegment(s)
  }
  if (Number.isNaN(input.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(input)
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? ''
  const y = get('year')
  const m = get('month')
  const d = get('day')
  return y && m && d ? `${y}-${m}-${d}` : ''
}

// ─── Bestandsnaam-bouwer ──────────────────────────────────────────────────────

export interface BuildStandardFilenameInput {
  /** Het leesbare type-segment (werkbon, factuur, planning, werkuren, ...). Verplicht. */
  type: string
  /** Klant- of BV-naam waarop het bestand slaat. Weglaten bij org-breed. */
  scope?: string | null
  /** Sorteerbare periode (zie `formatExportPeriode`). Weglaten als n.v.t. */
  periode?: string | null
  /** Sorteerbare datum (`yyyyMMdd`): kale kalenderdatum of `Date` (Europe/Amsterdam). */
  datum?: Date | string | null
  /**
   * FO-303-P2 — aanmaak-/exportdatum als LAATSTE deel (`yyyy-MM-dd`,
   * Europe/Amsterdam). Weglaten = geen aanmaakdatum-segment (achterwaarts
   * compatibel); geef een `Date`/`null` mee om het expliciet te sturen.
   */
  aanmaakdatum?: Date | string | null
  /** Extensie zonder punt (default 'xlsx'). */
  ext?: string
}

/**
 * FO-075 — de app-brede bestandsnaam `<type>_<scope>_<periode>_<datum>.<ext>`.
 * Eén sanitisatie (diacrieten-strip, Windows-/RFC 6266-veilig), `_` als
 * separator, lege/onbekende segmenten vallen weg (nooit een dubbele of rand-
 * underscore). De ene bron achter élke export- en download-naam in de app; de
 * werkbon-/factuur-/bibliotheek-namen leiden hier doorheen. FO-303-P2: een
 * optioneel `aanmaakdatum`-segment (`yyyy-MM-dd`) sluit de naam af.
 */
export function buildStandardFilename(input: BuildStandardFilenameInput): string {
  const segments = [
    sanitizeSegment(input.type),
    sanitizeSegment(input.scope),
    sanitizePeriode(input.periode),
    formatStandardDatum(input.datum),
    formatAanmaakdatum(input.aanmaakdatum),
  ].filter((s) => s.length > 0)
  const base = segments.join('_') || 'export'
  const cleanExt =
    (input.ext ?? 'xlsx')
      .replace(/^\.+/, '')
      .replace(/[^A-Za-z0-9]+/g, '')
      .toLowerCase() || 'bin'
  return `${base}.${cleanExt}`
}

// ─── Scope-segment (FO-351) ───────────────────────────────────────────────────

/**
 * FO-351 §6.3 — maximale lengte van het scope-segment. Lange afdelings- of
 * klantnamen maken de naam onleesbaar (en tikken tegen de Windows-padlimiet aan);
 * 40 tekens is ruim genoeg voor "Techniek en Onderhoud Zuid" en kort genoeg om
 * de periode + datum zichtbaar te houden in een verkenner-kolom.
 */
export const MAX_SCOPE_LENGTH = 40

/**
 * FO-351 §6.3 — kap een gesaneerd scope-segment af op `max` tekens, bij voorkeur
 * op een woordgrens (het laatste koppelteken binnen de limiet) zodat er geen half
 * woord overblijft: `Techniek en Onderhoud Zuid Nederland` → `Techniek-en-Onderhoud-Zuid`.
 *
 * Ligt die woordgrens in de eerste helft van het budget (of is er geen), dan zou
 * knippen te veel weggooien en kappen we hard af - denk aan één lang woord.
 * Invoer wordt verondersteld al door `sanitizeSegment` te zijn gegaan.
 */
export function truncateScopeSegment(value: string, max: number = MAX_SCOPE_LENGTH): string {
  if (value.length <= max) return value
  const head = value.slice(0, max)
  const lastDash = head.lastIndexOf('-')
  const cut = lastDash >= Math.floor(max / 2) ? head.slice(0, lastDash) : head
  return cut.replace(/-+$/, '')
}

/**
 * FO-351 §3.3 — scope-token voor een export zonder enkelvoudig filter: het
 * aantal + de eenheid, bv. `formatAantalScope(3, 'medewerkers')` → `3-medewerkers`.
 * De eenheid is NL-lowercase (de bestandsnaam is zichtbare copy). `aantal <= 0`
 * → lege string, zodat het scope-segment wegvalt i.p.v. `0-medewerkers`.
 */
export function formatAantalScope(aantal: number, eenheid: string): string {
  if (!Number.isFinite(aantal) || aantal <= 0) return ''
  const unit = sanitizeSegment(eenheid).toLowerCase()
  return unit ? `${Math.floor(aantal)}-${unit}` : ''
}

/**
 * FO-351 §2 — het scope-segment van de bellijst-export: het NL tab-label i.p.v.
 * de technische enum-sleutel. `medewerkers`/`klanten` zijn zelfsprekend, maar
 * `belangrijk` is een bijvoeglijk naamwoord zonder zelfstandig naamwoord en zegt
 * in een downloads-map niets; de tab bevat de belangrijke bedrijfsnummers.
 *
 * Gedeeld tussen de api (die de `Content-Disposition` zet) en de backoffice-client
 * (die dezelfde naam als vangnet gebruikt) - één bron, geen tweede naamlogica.
 */
export type BellijstBestandsnaamTab = 'medewerkers' | 'klanten' | 'belangrijk'

export const BELLIJST_TAB_BESTANDSNAAM_LABEL: Record<BellijstBestandsnaamTab, string> = {
  medewerkers: 'medewerkers',
  klanten: 'klanten',
  belangrijk: 'belangrijke-nummers',
}

// ─── Content-Disposition (FO-351) ─────────────────────────────────────────────

/**
 * FO-351 — `attachment; filename="<naam>"`: dé plek waar een download-header
 * wordt samengesteld. De naam zelf komt altijd uit de helpers hierboven; deze
 * functie zet er enkel de header-syntax omheen. Eén bron voorkomt dat elke route
 * of proxy zijn eigen quoting/encoding kiest (vóór FO-351 encodeerde de
 * xlsx-streamer de naam wél en de losse routes niet).
 *
 * Bewust hier en niet in de api: de Next-proxies moeten dezelfde header kunnen
 * bouwen als de api, en `@streep/utils` is de enige gedeelde, pure module.
 *
 * De naam wordt URL-geëncodeerd zodat een onverwacht non-ASCII teken de header
 * nooit kan breken (RFC 6266); de al ASCII-veilige namen uit deze module
 * (`[A-Za-z0-9._-]`) blijven letterlijk staan - `encodeURIComponent` laat die
 * tekens ongemoeid.
 */
export function contentDispositionAttachment(fileName: string): string {
  return `attachment; filename="${encodeURIComponent(fileName)}"`
}

export interface BuildExportFilenameInput {
  /** Het export-type (planning, relaties, bellijst, werkuren, ...). Verplicht. */
  wat: string
  /**
   * Waarop de export gescoped is (medewerker-, klant-, BV- of afdelingsnaam, of
   * een aantal-token uit `formatAantalScope`). Weglaten bij org-breed. Wordt op
   * `MAX_SCOPE_LENGTH` afgekapt (woordgrens, FO-351 §6.3).
   */
  scope?: string | null
  /** Sorteerbare periode (zie `formatExportPeriode`). Weglaten als n.v.t. */
  periode?: string | null
  /**
   * FO-303-P2 — aanmaak-/exportdatum als laatste deel (`yyyy-MM-dd`). Weglaten
   * (`undefined`) = **vandaag** (Europe/Amsterdam, het exportmoment); `null` =
   * bewust geen datum. Zo dragen alle ad-hoc exports hem zonder per-caller-werk.
   */
  aanmaakdatum?: Date | string | null
  /** Extensie zonder punt (default 'xlsx'). */
  ext?: string
}

/**
 * Bouw `wat_scope_periode_aanmaakdatum.ext` (FO-044-P1 + FO-303-P2). Een dunne
 * wrapper rond `buildStandardFilename`; sinds FO-303 sluit standaard de
 * aanmaak-/exportdatum (`yyyy-MM-dd`, Europe/Amsterdam) de naam af zodat élke
 * ad-hoc export chronologisch ordent. `aanmaakdatum` weglaten = het exportmoment
 * (`new Date()`); `null` = geen datum (achterwaarts compatibel). Lege segmenten
 * vallen weg en de output blijft ASCII-/RFC 6266-veilig.
 *
 * FO-351 — dit is DE bron voor élke download-naam van een data-export. Het
 * scope-segment wordt hier bewerkt: **lowercase** (§3/§6.1: `Daan van 't Hoff` →
 * `daan-van-t-hoff`) en afgekapt op `MAX_SCOPE_LENGTH` (woordgrens, §6.3). Het
 * periode-segment blijft ongemoeid - dat houdt zijn `W` (`2026-W24`).
 *
 * Beide bewerkingen zitten hier en NIET in `sanitizeSegment`/`buildStandardFilename`:
 * die dragen óók de werkbon-/factuur-/bibliotheek-namen, en die conventie valt
 * buiten FO-351 (§4). Zo blijft §6.1 ("sanitering blijft `sanitizeSegment`") waar
 * én komt de spec-uitkomst eruit.
 */
export function buildExportFilename(input: BuildExportFilenameInput): string {
  return buildStandardFilename({
    type: input.wat,
    scope: truncateScopeSegment(sanitizeSegment(input.scope).toLowerCase()),
    periode: input.periode,
    aanmaakdatum: input.aanmaakdatum === undefined ? new Date() : input.aanmaakdatum,
    ext: input.ext,
  })
}
